/**
 * affiliate-tracking.js
 *
 * Place this script in your Shopify theme under assets/ and include it in
 * theme.liquid (or layout/theme.liquid) just before </body>:
 *
 *   {{ 'affiliate-tracking.js' | asset_url | script_tag }}
 *
 * What it does:
 *  1. On every page load, checks window.localStorage for an existing
 *     affiliate record (_aff_data).
 *  2. If the current URL contains UTM + ref params and no stored record
 *     exists (or the ref differs), it immediately writes an optimistic record
 *     from URL params (so the cart and form inputs are populated with zero
 *     network delay), then silently calls the /track endpoint to validate and
 *     overwrite with the canonical server payload.
 *  3. The JSON response is saved to localStorage so it persists across pages
 *     without re-fetching (Shopify App Proxy strips Set-Cookie, so cookies
 *     cannot be used for cross-page persistence).
 *  4. On every page load (including the first) the script syncs the stored
 *     data to hidden line-item properties (_aff_*) so attribution stays on the
 *     order without showing in cart/checkout. Visible cart attributes (aff_*)
 *     are cleared so they no longer appear at checkout.
 *
 * The script also exposes window.AffiliateTracking so theme code and other
 * scripts can access the stored data or append the tracking token to their
 * own AJAX requests.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "_aff_data";
  var STORAGE_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

  /**
   * App Proxy / backend base URL for the track endpoint.
   * Adjust if your proxy subpath or domain differs.
   */
  var TRACK_ENDPOINT = "https://ranmerch.com/apps/randamu-api/api/affiliates/track";

  var VISIBLE_AFF_ATTRIBUTE_KEYS = [
    "aff_tracking_id",
    "aff_utm_source",
    "aff_utm_campaign",
    "aff_utm_medium",
    "aff_utm_term",
    "aff_utm_content",
  ];

  var VISIBLE_AFF_PROPERTY_KEYS = VISIBLE_AFF_ATTRIBUTE_KEYS.slice();

  // ---------------------------------------------------------------------------
  // localStorage helpers
  // ---------------------------------------------------------------------------

  /** Persist affiliate data with a fresh expiry timestamp. */
  function saveToStorage(data) {
    try {
      var record = {
        data: data,
        expires_at: Date.now() + STORAGE_EXPIRY_MS,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {
      console.warn("[Affiliate] Could not write to localStorage:", e);
    }
  }

  /**
   * Read affiliate data from localStorage.
   * Returns null when absent or when the record has expired.
   */
  function loadFromStorage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var record = JSON.parse(raw);
      if (!record || !record.data || !record.expires_at) return null;
      if (Date.now() > record.expires_at) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return record.data;
    } catch (e) {
      return null;
    }
  }

  /** Remove the stored affiliate record immediately. */
  function clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* swallow */
    }
  }

  // ---------------------------------------------------------------------------
  // URL helpers
  // ---------------------------------------------------------------------------

  /**
   * Extracts all affiliate-relevant parameters from the current page URL.
   * Returns a plain object with every key defined (value is null when absent).
   */
  function getAffiliateParamsFromUrl() {
    var params = new URLSearchParams(window.location.search);
    return {
      ref: params.get("ref"),
      utm_source: params.get("utm_source"),
      utm_campaign: params.get("utm_campaign"),
      utm_medium: params.get("utm_medium"),
      utm_term: params.get("utm_term"),
      utm_content: params.get("utm_content"),
      redirect_to: params.get("redirect_to"),
    };
  }

  /**
   * Detect whether the current URL carries affiliate tracking parameters.
   * Requires at least one UTM param AND a ref token, which is the minimum
   * needed for the /track endpoint to accept the request.
   */
  function currentUrlHasTrackingParams() {
    var p = getAffiliateParamsFromUrl();
    var hasRef = Boolean(p.ref);
    var hasUtm = Boolean(p.utm_source || p.utm_campaign || p.utm_medium || p.utm_term || p.utm_content);
    return hasRef && hasUtm;
  }

  // ---------------------------------------------------------------------------
  // Hidden property helpers
  // ---------------------------------------------------------------------------

  /** Build hidden (_-prefixed) line-item properties from affiliate data. */
  function buildHiddenProperties(data) {
    var properties = {};
    if (data.tracking_id) properties._aff_tracking_id = data.tracking_id;
    if (data.utm_source) properties._aff_utm_source = data.utm_source;
    if (data.utm_campaign) properties._aff_utm_campaign = data.utm_campaign;
    if (data.utm_medium) properties._aff_utm_medium = data.utm_medium;
    if (data.utm_term) properties._aff_utm_term = data.utm_term;
    if (data.utm_content) properties._aff_utm_content = data.utm_content;
    return properties;
  }

  /** Strip legacy visible aff_* keys from a properties object. */
  function stripVisibleAffiliateProperties(properties) {
    var cleaned = Object.assign({}, properties || {});
    VISIBLE_AFF_PROPERTY_KEYS.forEach(function (key) {
      delete cleaned[key];
    });
    return cleaned;
  }

  // ---------------------------------------------------------------------------
  // Cart attributes (clear legacy visible values only)
  // ---------------------------------------------------------------------------

  /**
   * Clear legacy visible cart attributes so they no longer appear in checkout.
   * Affilate attribution now lives on hidden line-item properties instead.
   */
  function clearVisibleCartAttributes() {
    var attributes = {};
    VISIBLE_AFF_ATTRIBUTE_KEYS.forEach(function (key) {
      attributes[key] = "";
    });

    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function (err) {
      console.warn("[Affiliate] Could not clear cart attributes:", err);
    });
  }

  // ---------------------------------------------------------------------------
  // Form inputs + cart line items
  // ---------------------------------------------------------------------------

  /**
   * Populate the hidden affiliate inputs inside #affiliate-line-item-properties
   * so Add to Cart / Buy It Now forms carry _aff_* properties (hidden in
   * cart and checkout, still saved on the order).
   */
  function updateLineItemPropertyInputs(data) {
    var container = document.getElementById("affiliate-line-item-properties");
    if (!container) return;

    var mapping = {
      _aff_tracking_id: data.tracking_id,
      _aff_utm_source: data.utm_source,
      _aff_utm_campaign: data.utm_campaign,
      _aff_utm_medium: data.utm_medium,
      _aff_utm_term: data.utm_term,
      _aff_utm_content: data.utm_content,
    };

    for (var key in mapping) {
      if (!Object.prototype.hasOwnProperty.call(mapping, key)) continue;
      var value = mapping[key];
      var input = container.querySelector('input[name="properties[' + key + ']"]');
      if (input && value) {
        input.value = value;
      }
    }
  }

  /**
   * Apply hidden _aff_* properties to every item already in the cart, and
   * remove any legacy visible aff_* properties from those lines.
   */
  function applyAffiliateToCartLines(data) {
    var hiddenProperties = buildHiddenProperties(data);
    if (Object.keys(hiddenProperties).length === 0) return;

    fetch("/cart.js")
      .then(function (response) {
        if (!response.ok) throw new Error("Cart fetch failed: " + response.status);
        return response.json();
      })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return Promise.resolve();

        return cart.items.reduce(function (chain, item) {
          return chain.then(function () {
            var existing = stripVisibleAffiliateProperties(item.properties || {});
            var needsUpdate = false;

            Object.keys(hiddenProperties).forEach(function (key) {
              if (existing[key] !== hiddenProperties[key]) {
                needsUpdate = true;
              }
            });

            VISIBLE_AFF_PROPERTY_KEYS.forEach(function (key) {
              if (item.properties && item.properties[key]) {
                needsUpdate = true;
              }
            });

            if (!needsUpdate) return Promise.resolve();

            var merged = Object.assign({}, existing, hiddenProperties);

            return fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: item.key,
                quantity: item.quantity,
                properties: merged,
              }),
            }).then(function (response) {
              if (!response.ok) throw new Error("Cart change failed: " + response.status);
              return response.json();
            });
          });
        }, Promise.resolve());
      })
      .catch(function (err) {
        console.warn("[Affiliate] Could not update cart line properties:", err);
      });
  }

  /**
   * Sync affiliate data to hidden form inputs + cart lines, and clear legacy
   * visible cart attributes.
   */
  function syncAffiliateData(data) {
    clearVisibleCartAttributes();
    updateLineItemPropertyInputs(data);
    applyAffiliateToCartLines(data);
  }

  // ---------------------------------------------------------------------------
  // Track endpoint call
  // ---------------------------------------------------------------------------

  /**
   * Silently calls the server-side /track endpoint with the affiliate params
   * extracted from the current URL.
   *
   * Strategy:
   *   1. Write an OPTIMISTIC record from URL params immediately (zero latency).
   *      This ensures the cart and form inputs are populated even if the user
   *      clicks checkout before the network request completes.
   *   2. Fire the network request in the background.
   *   3. On success, overwrite the optimistic record with the canonical server
   *      payload and re-sync cart + form inputs.
   */
  function callTrackEndpoint() {
    var affParams = getAffiliateParamsFromUrl();

    // --- Step 1: Optimistic write (immediate, no network wait) ---------------
    var optimistic = {
      tracking_id: affParams.ref,
      utm_source: affParams.utm_source,
      utm_campaign: affParams.utm_campaign,
      utm_medium: affParams.utm_medium,
      utm_term: affParams.utm_term,
      utm_content: affParams.utm_content,
    };
    saveToStorage(optimistic);
    syncAffiliateData(optimistic);

    // --- Step 2: Server confirmation (async) ---------------------------------
    var query = new URLSearchParams();
    if (affParams.ref) query.set("ref", affParams.ref);
    if (affParams.utm_source) query.set("utm_source", affParams.utm_source);
    if (affParams.utm_campaign) query.set("utm_campaign", affParams.utm_campaign);
    if (affParams.utm_medium) query.set("utm_medium", affParams.utm_medium);
    if (affParams.utm_term) query.set("utm_term", affParams.utm_term);
    if (affParams.utm_content) query.set("utm_content", affParams.utm_content);
    var trackUrl = TRACK_ENDPOINT + "?" + query.toString();

    fetch(trackUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    })
      .then(function (res) {
        return res.json().then(function (body) {
          return { ok: res.ok, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok || !result.body.success) {
          console.warn("[Affiliate] Track endpoint returned non-success — keeping optimistic record:", result.body);
          return;
        }

        saveToStorage(result.body);
        syncAffiliateData(result.body);
      })
      .catch(function (err) {
        // Non-critical. The optimistic record written in Step 1 is still valid
        // and will be used for cart attributes and form submissions.
        console.warn("[Affiliate] Could not reach track endpoint — optimistic record retained:", err);
      });
  }

  // ---------------------------------------------------------------------------
  // Public API  (window.AffiliateTracking)
  // ---------------------------------------------------------------------------

  /**
   * Appends the stored tracking token as an `aff_token` query parameter to a
   * given URL string. Returns the original URL unchanged when no token is
   * stored. Use this to attribute AJAX requests you make to proxy endpoints.
   *
   * @param {string} url - The URL to annotate.
   * @returns {string}
   *
   * @example
   * var url = window.AffiliateTracking.appendTrackingToken('/apps/myapp/api/cart');
   * // => '/apps/myapp/api/cart?aff_token=abc123...'
   */
  function appendTrackingToken(url) {
    var stored = loadFromStorage();
    if (!stored || !stored.tracking_id) return url;
    try {
      var base = url.indexOf("://") !== -1 ? url : window.location.origin + url;
      var parsed = new URL(base);
      parsed.searchParams.set("aff_token", stored.tracking_id);
      return url.indexOf("://") !== -1 ? parsed.toString() : parsed.pathname + parsed.search + parsed.hash;
    } catch (e) {
      return url;
    }
  }

  window.AffiliateTracking = {
    /** Returns the currently stored affiliate payload, or null. */
    getData: loadFromStorage,
    /** Manually clear the stored affiliate record. */
    clear: clearStorage,
    /** Append the stored tracking token to a URL. */
    appendTrackingToken: appendTrackingToken,
  };

  // ---------------------------------------------------------------------------
  // Main entry point
  // ---------------------------------------------------------------------------

  /** Runs `callback` after the next paint, once the browser is idle (or within 2s). */
  function afterPaintIdle(callback) {
    requestAnimationFrame(function () {
      setTimeout(function () {
        if (window.requestIdleCallback) window.requestIdleCallback(callback, { timeout: 2000 });
        else callback();
      }, 0);
    });
  }

  function init() {
    // -------------------------------------------------------------------------
    // PRIORITY 1: Sync any already-stored data immediately (synchronous path).
    //
    // This populates hidden form inputs and cart line properties BEFORE any
    // user interaction is possible, eliminating the race condition where a
    // customer clicks "Checkout" before the async work resolves.
    // -------------------------------------------------------------------------
    var stored = loadFromStorage();
    if (stored) {
      syncAffiliateData(stored);
    } else {
      // Still clear any leftover visible cart attributes from older sessions. Nothing on the
      // page depends on it, so it waits until the page has painted and the browser is idle.
      afterPaintIdle(clearVisibleCartAttributes);
    }

    // -------------------------------------------------------------------------
    // PRIORITY 2: If the current URL has fresh tracking params, decide whether
    // to fire a new track call.
    //
    // We fire a new call when:
    //   (a) There is no stored record at all, OR
    //   (b) The incoming ref differs from the stored tracking_id
    //       (i.e. the visitor followed a different affiliate link).
    //
    // callTrackEndpoint() handles the optimistic-write pattern internally, so
    // calling it here is safe — it will immediately overwrite stale stored data
    // and sync to the cart/form before awaiting the server response.
    // -------------------------------------------------------------------------
    if (currentUrlHasTrackingParams()) {
      var incomingRef = getAffiliateParamsFromUrl().ref;
      if (!stored || stored.tracking_id !== incomingRef) {
        callTrackEndpoint();
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
